import { ChevronRight, Plus } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Item, ItemContent, ItemGroup, ItemTitle } from '@/components/ui/item'
import { type ClaimListRow, INSURED_NAME, sortForList, STATUS_LABELS } from './claimsData'
import { ClaimsEmptyState } from './ClaimsEmptyState'

/**
 * Every claim on the account, each stating its own status. Sorted so anything
 * blocked on the customer sits at the top, then earliest stage first.
 */
export function ClaimsList({ claims }: { claims: ClaimListRow[] }) {
  const rows = sortForList(claims)

  return (
    <div className="max-w-2xl">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-ink-400 font-mono text-xs uppercase">Claims</div>
          <h1 className="mt-1 text-2xl font-bold tracking-[-0.01em]">Your claims</h1>
        </div>
        {claims.length > 0 && (
          <Button size="sm">
            <Plus />
            Report a claim
          </Button>
        )}
      </div>
      <p className="text-ink-500 mt-1 mb-5 text-sm">
        {claims.length > 0 ? `${claims.length} claims for ${INSURED_NAME}` : INSURED_NAME}
      </p>

      {claims.length === 0 ? (
        <ClaimsEmptyState />
      ) : (
        <ItemGroup className="border-ink-200 bg-surface divide-ink-200 divide-y overflow-hidden rounded-md border">
          {rows.map((row) => (
            <Item key={row.ref} asChild size="sm" className="rounded-none">
              <button type="button" className="hover:bg-ink-50 w-full text-left">
                <span className="text-ink-400 hidden w-16 shrink-0 font-mono text-xs sm:block">
                  {row.ref}
                </span>
                <ItemContent className="min-w-0 gap-0.5">
                  <ItemTitle className="w-full gap-2">
                    <span className="truncate">{row.description}</span>
                    {row.needsYou && (
                      <Badge className="border-(--amber)/40 bg-(--amber)/15 text-(--amber-text)">
                        Needs you
                      </Badge>
                    )}
                  </ItemTitle>
                  <span className="text-ink-500 truncate text-xs">
                    <span className="sm:hidden">{STATUS_LABELS[row.status]} · </span>
                    {row.stageNote}
                  </span>
                </ItemContent>
                <span className="hidden w-36 shrink-0 text-xs font-medium sm:block">
                  {STATUS_LABELS[row.status]}
                </span>
                <span className="text-ink-400 w-14 shrink-0 text-right font-mono text-xs">{row.date}</span>
                <ChevronRight className="text-ink-400 size-3.5 shrink-0" />
              </button>
            </Item>
          ))}
        </ItemGroup>
      )}
    </div>
  )
}
